import { useState, useEffect } from "react";
import api from "../../services/api";
import { CheckCircle2, ShieldCheck, WalletCards, CreditCard, Sparkles } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

export default function InstituteBilling() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    // Load Razorpay script
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
    
    fetchPlans();
    
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await api.get("/saas/plans");
      setPlans(res.data);
    } catch (err) {
      toast.error("Failed to load billing plans");
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (plan) => {
    try {
      setProcessing(true);
      const res = await api.post("/saas/subscribe", { plan_id: plan.id });
      
      if (!res.data.order_id) {
        toast.success("Successfully subscribed to free plan!");
        setTimeout(() => {
          window.location.href = "/institute/dashboard";
        }, 1500);
        return;
      }

      const options = {
        key: res.data.key,
        amount: res.data.amount,
        currency: res.data.currency,
        name: "Skillzy LMS",
        description: `Subscription to ${plan.name} Plan`,
        order_id: res.data.order_id,
        handler: async (response) => {
          try {
            await api.post("/saas/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            toast.success("Payment successful! Plan activated.");
            setTimeout(() => {
              window.location.href = "/institute/dashboard";
            }, 1500);
          } catch (verifyErr) {
            toast.error("Payment verification failed.");
          }
        },
        theme: { color: "#4f46e5" }
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        toast.error("Payment failed or cancelled.");
      });
      rzp.open();
      
    } catch (err) {
      toast.error(err.response?.data?.detail || "Subscription failed");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <Toaster position="top-right" />
      <div className="flex flex-col items-center justify-center text-center space-y-4 py-8">
        <div className="rounded-2xl bg-indigo-50 p-4 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
          <WalletCards size={32} />
        </div>
        <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white">
          Upgrade Your Institute
        </h1>
        <p className="text-slate-500 max-w-2xl text-lg">
          Choose a SaaS plan to unlock more students, advanced analytics, and custom white-labeling.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center p-12"><div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600/20 border-t-indigo-600" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {plans.map((plan, idx) => (
            <div key={plan.id} className={`relative flex flex-col rounded-3xl bg-white p-8 shadow-sm dark:bg-slate-900 border-2 transition-all hover:-translate-y-1 ${idx === 1 ? 'border-indigo-500 shadow-xl shadow-indigo-500/10 scale-105' : 'border-slate-200 dark:border-slate-800'}`}>
              {idx === 1 && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-indigo-500 px-4 py-1 text-xs font-black uppercase tracking-widest text-white shadow-sm flex items-center gap-1">
                  <Sparkles size={14}/> Most Popular
                </div>
              )}
              
              <div className="mb-6 text-center">
                <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">{plan.name}</h3>
                <div className="mt-4 flex items-baseline justify-center text-slate-900 dark:text-white">
                  <span className="text-5xl font-extrabold tracking-tight">₹{plan.price}</span>
                  <span className="ml-1 text-sm font-bold text-slate-500">/{plan.billing_cycle === 'monthly' ? 'mo' : 'yr'}</span>
                </div>
              </div>
              
              <ul className="mb-8 flex-1 space-y-4 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex items-start gap-3 font-bold text-slate-900 dark:text-slate-200">
                  <ShieldCheck size={20} className="text-emerald-500 shrink-0" />
                  Max {plan.max_students} Active Students
                </li>
                {plan.features?.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3 font-medium">
                    <CheckCircle2 size={20} className="text-indigo-500 shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              
              <button 
                onClick={() => handleSubscribe(plan)}
                disabled={processing}
                className={`w-full rounded-2xl py-4 text-sm font-black uppercase tracking-widest transition-all ${idx === 1 ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md shadow-indigo-600/20' : 'bg-slate-100 text-slate-900 hover:bg-slate-200 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700'}`}
              >
                {processing ? "Processing..." : `Subscribe to ${plan.name}`}
              </button>
            </div>
          ))}
          {plans.length === 0 && (
            <div className="col-span-full text-center text-slate-500">No active billing plans found. Contact support.</div>
          )}
        </div>
      )}
    </div>
  );
}
